import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const ACTIVICA_PIXEL =
    '"FT Activica Pixel", "FT Activica Normal", monospace';

export default function Footer() {
    const mapHostRef = useRef(null);

    // Fetch the world-map SVG, inline it, then randomly tag ~140 of its
    // 2700+ path dots with a twinkle class on a staggered animation-delay
    // so the map subtly comes alive without adding any extra elements.
    useEffect(() => {
        const host = mapHostRef.current;
        if (!host) return;
        let cancelled = false;
        fetch("/assets/footer-map.svg")
            .then((r) => r.text())
            .then((svgText) => {
                if (cancelled) return;
                host.innerHTML = svgText;
                const svg = host.querySelector("svg");
                if (!svg) return;
                svg.setAttribute("preserveAspectRatio", "xMidYMid slice");
                svg.style.width = "100%";
                svg.style.height = "100%";
                svg.style.display = "block";

                const paths = Array.from(svg.querySelectorAll("path"));
                // Shuffle (Fisher-Yates) then pick the first N to twinkle.
                for (let i = paths.length - 1; i > 0; i -= 1) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [paths[i], paths[j]] = [paths[j], paths[i]];
                }
                const N = Math.min(140, paths.length);
                for (let i = 0; i < N; i += 1) {
                    const p = paths[i];
                    p.classList.add("vc-map-twinkle");
                    p.style.animationDelay = `${(Math.random() * 4).toFixed(2)}s`;
                    p.style.animationDuration = `${(2.4 + Math.random() * 2.2).toFixed(2)}s`;
                }
            })
            .catch(() => {
                /* Fall back to a plain <img> if fetch fails */
                host.innerHTML =
                    '<img src="/assets/footer-map.svg" alt="" style="width:100%;height:100%;object-fit:cover;" />';
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <footer
            data-testid="footer"
            className="relative w-full overflow-hidden bg-background flex flex-col"
            style={{ height: "100vh", maxHeight: "100vh" }}
        >
            {/* Dotted world map backdrop (inline SVG so individual dots
                can twinkle). */}
            <div
                ref={mapHostRef}
                aria-hidden
                data-testid="footer-map"
                className="absolute inset-0 pointer-events-none select-none"
                style={{ opacity: 0.6 }}
            />

            {/* Subtle radial vignette to focus the centre copy */}
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(ellipse at center, rgba(10,10,10,0) 0%, rgba(10,10,10,0.55) 60%, rgba(10,10,10,0.9) 100%)",
                }}
            />

            {/* Centre content block - flex-1 to fill available height above wordmark */}
            <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 pt-16 md:pt-20 pb-6">
                <h2
                    data-testid="footer-heading"
                    style={{
                        fontFamily: ACTIVICA_PIXEL,
                        fontWeight: 400,
                        fontSize: "clamp(2rem, 5.4vw, 5rem)",
                        lineHeight: 1.05,
                        letterSpacing: "0.02em",
                        color: "#FFFCE1",
                        textTransform: "uppercase",
                    }}
                >
                    Coming Soon To
                </h2>
                <h2
                    style={{
                        fontFamily: ACTIVICA_PIXEL,
                        fontWeight: 400,
                        fontSize: "clamp(2rem, 5.4vw, 5rem)",
                        lineHeight: 1.05,
                        letterSpacing: "0.02em",
                        marginTop: "0.4em",
                        textTransform: "uppercase",
                        background:
                            "linear-gradient(177.93deg, #07E6F6 30%, #003C9D 120%)",
                        WebkitBackgroundClip: "text",
                        backgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        color: "transparent",
                    }}
                >
                    A City Near You
                </h2>

                <Link
                    to="/apply"
                    data-testid="footer-cta-apply"
                    className="inline-flex items-center justify-center hover:brightness-110 transition-[filter] mt-10 md:mt-14"
                    style={{
                        padding: "9.2335px 23.0837px",
                        gap: "9.23px",
                        minWidth: "253px",
                        height: "46.47px",
                        background:
                            "linear-gradient(180deg, #EDE9C8 11.84%, #807F6F 138.81%)",
                        boxShadow:
                            "inset 0 4px 7.1px #FFFFFF, inset 0 -1px 15.1px #FFFFFF",
                        borderRadius: "11.5px",
                        fontFamily:
                            '"Figtree", "Inter", system-ui, sans-serif',
                        fontWeight: 700,
                        fontSize: "16.1px",
                        lineHeight: "28px",
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        color: "#0E100F",
                        textDecoration: "none",
                    }}
                >
                    Apply Now
                </Link>
            </div>

            {/* Giant pixel VIBECITY wordmark anchored to the bottom of the
                flex column - sits flush against the content block. Height
                clamped so it never eats the heading/CTA on small screens. */}
            <div
                data-testid="footer-wordmark"
                className="relative z-10 w-full pointer-events-none select-none"
                style={{
                    overflow: "hidden",
                    lineHeight: 0,
                    maxHeight: "32vh",
                }}
            >
                <img
                    src="/assets/footer-logo.svg"
                    alt="VIBECITY"
                    className="block w-full h-auto"
                    style={{
                        maxHeight: "32vh",
                        objectFit: "cover",
                        objectPosition: "center top",
                    }}
                />
            </div>
        </footer>
    );
}
